import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Slider,
  Box,
  Typography,
} from "@mui/material";
import { getCroppedImg } from "../helper/cropImage";

const ASPECT_OPTIONS = [
  { id: "free", label: "Free-form (Original)", aspect: null },
  { id: "square", label: "Square (1:1)", aspect: 1 },
  { id: "rectangle", label: "Rectangle (16:9)", aspect: 16 / 9 },
  { id: "banner", label: "Wide (2:1)", aspect: 2 / 1 },
];

const ImageCropModal = ({ open, imageSrc, onClose, onCropComplete }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspectMode, setAspectMode] = useState("free"); // "free" | "square" | "rectangle" | "banner"
  const [naturalAspect, setNaturalAspect] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const handleMediaLoaded = (mediaSize) => {
    if (mediaSize?.naturalWidth && mediaSize?.naturalHeight) {
      setNaturalAspect(mediaSize.naturalWidth / mediaSize.naturalHeight);
    }
  };

  const getActiveAspect = () => {
    if (aspectMode === "square") return 1;
    if (aspectMode === "rectangle") return 16 / 9;
    if (aspectMode === "banner") return 2 / 1;
    // For free-form, use the natural aspect ratio of the uploaded image
    return naturalAspect || undefined;
  };

  const handleCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleDone = async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      onCropComplete(croppedImage);
      onClose();
    } catch (err) {
      console.error("Error cropping image, using original:", err);
      onCropComplete(imageSrc);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      sx={{
        zIndex: 99999,
        "& .MuiBackdrop-root": {
          zIndex: 99998,
        },
        "& .MuiDialog-container": {
          zIndex: 99999,
        },
        "& .MuiPaper-root": {
          zIndex: 99999,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: "1px solid #f1f5f9" }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: "#1e293b" }}>
          Crop Image / Logo
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.85rem" }}>
          Select aspect ratio: Free-form (original), Square, or Rectangle
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Shape / Aspect Ratio Selection Buttons */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
          {ASPECT_OPTIONS.map((opt) => (
            <Button
              key={opt.id}
              size="small"
              variant={aspectMode === opt.id ? "contained" : "outlined"}
              color="primary"
              onClick={() => {
                setAspectMode(opt.id);
                setZoom(1);
                setCrop({ x: 0, y: 0 });
              }}
              sx={{
                borderRadius: "20px",
                textTransform: "none",
                fontWeight: aspectMode === opt.id ? 700 : 500,
                fontSize: "0.8rem",
                px: 1.5,
              }}
            >
              {opt.label}
            </Button>
          ))}
        </Box>

        {/* Cropper Box */}
        <Box
          sx={{
            position: "relative",
            height: 340,
            width: "100%",
            borderRadius: "12px",
            overflow: "hidden",
            backgroundColor: "#0f172a",
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={getActiveAspect()}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={handleCropComplete}
            onMediaLoaded={handleMediaLoaded}
            showGrid={true}
          />
        </Box>

        {/* Zoom & Rotate Controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1, px: 1 }}>
          <Typography variant="caption" sx={{ color: "#475569", fontWeight: 600, minWidth: 45 }}>
            Zoom:
          </Typography>
          <Slider
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e, z) => setZoom(z)}
            size="small"
            sx={{ flex: 1 }}
          />
          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, minWidth: 35 }}>
            {Math.round(zoom * 100)}%
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={() => setRotation((prev) => (prev + 90) % 360)}
            sx={{ textTransform: "none", fontSize: "0.75rem", p: 0.5, minWidth: "auto", color: "#64748b" }}
            title="Rotate 90°"
          >
            ↻ Rotate
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, borderTop: "1px solid #f1f5f9" }}>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: "none", fontWeight: 600 }}>
          Cancel
        </Button>
        <Button onClick={handleDone} variant="contained" color="primary" sx={{ textTransform: "none", fontWeight: 700, px: 3 }}>
          Crop & Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropModal;
